import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Info as InfoIcon } from 'react-feather'
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core'

import { useForm } from 'react-hook-form'
import { BaseDialog, LoadingFeedback } from 'components'
import { EvaluateLargeScaleDialog } from './components'

import useIncidentManegement from 'hooks/useIncidentManegement'
import useFetch from 'hooks/useFetch'

import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'

import constants from 'constants/index'
import useStyles from './styles'

const Impact = () => {
  const classes = useStyles()
  const snackbar = useSnackbar()

  const [loading, setLoading] = useState(false)

  const [isTooltipDialogOpen, setIsTooltipDialogOpen] = useState(false)

  const [isEvaluateLargeScaleDialogOpen, setIsEvaluateLargeScaleDialogOpen] =
    useState(false)
  const hasOpenedEvaluateLargeScaleDialog = useRef(false)

  const {
    LARGE_SCALE_ANSWER_LABELS,
    SPECIFIC_CRITERIA_ANSWER_LABELS,
    SIGNIFICANTLY_AFFECT_ANSWER_LABELS,
    IMPACT_ANSWER_LABELS,
  } = constants.incidentHighRisk

  const { incident, wantsToAdvanceStep, resetAdvanceStep, handleNext } =
    useIncidentManegement()

  const { response, refresh } = useFetch(service.dponet.incidents.getHighRisk, {
    incidentId: incident?.id,
  })

  const highRiskImpact = response?.data
  const largeScaleLabel =
    LARGE_SCALE_ANSWER_LABELS[highRiskImpact?.isLargeScale] || '-'
  const significantlyAffectLabel =
    SIGNIFICANTLY_AFFECT_ANSWER_LABELS[
      highRiskImpact?.significantlyAffectFundamentalInterests
    ] || '-'
  const specificCriteriaLabel =
    SPECIFIC_CRITERIA_ANSWER_LABELS[highRiskImpact?.hasSpecificCriteria] || '-'
  const impactLabel = IMPACT_ANSWER_LABELS[highRiskImpact?.isHighRisk] || '-'

  const wasLargeScaleEvaluated = highRiskImpact?.wasLargeScaleEvaluated === true

  const { handleSubmit } = useForm()

  const handleOpenTooltipDialog = () => {
    setIsTooltipDialogOpen(true)
  }

  const handleCloseTooltipDialog = () => {
    setIsTooltipDialogOpen(false)
  }

  const handleOpenEvaluateLargeScaleDialog = () => {
    setIsEvaluateLargeScaleDialogOpen(true)
  }

  const handleCloseEvaluateLargeScaleDialog = () => {
    setIsEvaluateLargeScaleDialogOpen(false)
  }

  const handleGenerateReport = async () => {
    setLoading(true)
    try {
      const response = await service.dponet.incidents.highRiskToExcel({
        incidentId: incident?.id,
      })

      const { url } = response?.data || {}
      window.open(url)
    } catch (error) {
      snackbar.open({
        message: 'Ocorreu um erro ao tentar exportar o relatório',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      await service.dponet.incidents.updateHighRisk({
        incidentId: incident?.id,
        wasLargeScaleEvaluated: true,
        isLargeScale: data.isLargeScale,
      })
      refresh()
      handleCloseEvaluateLargeScaleDialog()
    } catch (error) {
      snackbar.open({
        message: 'Ocorreu um erro ao enviar as respostas',
        variant: 'error',
      })
    } finally {
      setLoading(false)
      resetAdvanceStep()
    }
  }

  useEffect(() => {
    if (hasOpenedEvaluateLargeScaleDialog.current) {
      return
    }

    if (largeScaleLabel === 'Avaliar') {
      hasOpenedEvaluateLargeScaleDialog.current = true
      handleOpenEvaluateLargeScaleDialog()
    }
  }, [largeScaleLabel])

  useEffect(() => {
    const handleAdvance = async () => {
      if (!wantsToAdvanceStep) return

      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })

      if (largeScaleLabel === 'Avaliar') {
        handleOpenEvaluateLargeScaleDialog()
        resetAdvanceStep()
        return
      }

      try {
        await service.dponet.incidents.concludeStep({
          incidentId: incident?.id,
          step: 'high_risk',
        })
      } catch {
        snackbar.open({
          message: 'Ocorreu um erro ao concluir o step',
          variant: 'error',
        })
      }

      resetAdvanceStep()
      handleNext()
    }

    handleAdvance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsToAdvanceStep])

  return (
    <Box className={classes.summaryContainer}>
      <LoadingFeedback open={loading} />
      <Paper className={classes.summaryCard}>
        <Box className={classes.summaryHeader}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography className={classes.summaryTitle}>
                Resumo da Avaliação - Tratamento de Alto Risco
              </Typography>
            </Grid>
            <Grid item xs />
            <Grid item>
              <Grid
                container
                direction="column"
                spacing={1}
                alignItems="flex-end"
                wrap="nowrap"
              >
                {wasLargeScaleEvaluated && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOpenEvaluateLargeScaleDialog}
                      className={classes.largeScaleButton}
                      fullWidth
                      disableElevation
                    >
                      Reavaliar Larga Escala
                    </Button>
                  </Grid>
                )}
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateReport}
                    className={classes.summaryButton}
                    fullWidth
                    disableElevation
                    disabled={largeScaleLabel === 'Avaliar'}
                  >
                    Gerar Relatório
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.row}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={10} md={10} lg={10}>
              <Typography className={classes.rowTitle}>
                É tratamento de larga escala?
              </Typography>
              <Typography className={classes.rowSubtitle}>
                Volume significativo de dados ou titulares
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={2}
              md={2}
              lg={2}
              className={classes.chipColumn}
            >
              <Chip
                className={classes.chip}
                label={
                  wasLargeScaleEvaluated
                    ? `Avaliado - ${largeScaleLabel}`
                    : largeScaleLabel
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.row}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={10} md={10} lg={10}>
              <Typography className={classes.rowTitle}>
                Afeta interesses e direitos fundamentais?
              </Typography>
              <Typography className={classes.rowSubtitle}>
                Impacto potencial na privacidade
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={2}
              md={2}
              lg={2}
              className={classes.chipColumn}
            >
              <Chip label={significantlyAffectLabel} className={classes.chip} />
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.row}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={10} md={10} lg={10}>
              <Typography className={classes.rowTitle}>
                Possui um dos critérios específicos?
              </Typography>
              <Typography className={classes.rowSubtitle}>
                Critérios definidos pela autoridade nacional
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={2}
              md={2}
              lg={2}
              className={classes.chipColumn}
            >
              <Chip label={specificCriteriaLabel} className={classes.chip} />
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.finalRow}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={10}>
              <Typography
                className={
                  impactLabel === 'Sim'
                    ? classes.finalTitleYes
                    : impactLabel === 'Não'
                    ? classes.finalTitleNo
                    : classes.finalTitleNo
                }
              >
                O TRATAMENTO É DE ALTO RISCO?
              </Typography>
              <Typography
                className={
                  impactLabel === 'Sim'
                    ? classes.finalSubtitleYes
                    : impactLabel === 'Não'
                    ? classes.finalSubtitleNo
                    : classes.finalSubtitleNo
                }
              >
                Conclusão final baseada nos critérios acima
              </Typography>
            </Grid>
            <Grid item xs={12} sm={2} className={classes.chipColumn}>
              <Typography
                className={
                  impactLabel === 'Sim'
                    ? classes.resultLabelYes
                    : impactLabel === 'Não'
                    ? classes.resultLabelNo
                    : classes.resultLabelNo
                }
              >
                RESULTADO
                <IconButton
                  onClick={handleOpenTooltipDialog}
                  className={classes.infoButton}
                >
                  <InfoIcon size={10} />
                </IconButton>
              </Typography>
              <Chip label={impactLabel} className={classes.chip} />
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <BaseDialog
        open={isTooltipDialogOpen}
        setOpen={setIsTooltipDialogOpen}
        title="Como é calculada a classificação de alto risco?"
        actionButtonText="Fechar"
        actionButton={handleCloseTooltipDialog}
      >
        <Typography>
          A classificação como alto risco depende da combinação dos critérios
          avaliados.
        </Typography>
        <Box mt={1} />
        <Typography>
          Se os critérios específicos forem marcados como “Não”, o tratamento
          não será considerado de alto risco.
        </Typography>
        <Box mt={1} />
        <Typography>
          Contudo, se os critérios específicos forem marcados como “Sim” e pelo
          menos uma das condições anteriores também estiver como “Sim”, o
          tratamento será classificado como alto risco.
        </Typography>
      </BaseDialog>
      <form onSubmit={handleSubmit(onSubmit)}>
        <EvaluateLargeScaleDialog
          open={isEvaluateLargeScaleDialogOpen}
          onClose={handleCloseEvaluateLargeScaleDialog}
          onSubmit={onSubmit}
        />
      </form>
    </Box>
  )
}

Impact.propTypes = {
  refresh: PropTypes.func,
}

export default Impact
